import React, { useCallback, ReactElement } from 'react';
import { useNavigate } from 'react-router';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import Image from '../../../components/image/image.component';
import Widget from '../../../components/widget/widget.component';
import TextBold from '../../../components/text-bold/text-bold.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import IconBlockExpired from '../../../components/icon-block-expired/icon-block-expired.component';
import ScreenService from '../../../services/screen-service';
import FormatService from '../../../services/format-service';
import { ROUTE_DEAL } from '../../../constants/system/routes';
import { WIDGET_NAME_USER_DEAL_TASKS } from '../../../constants/system/widget-names';
import { QUERY_PARAM_CONTACT_ID, QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { TAB_NAME_DUE_DILIGENCE } from '../../../constants/system/tab-names';
import { DEAL_VIEW } from '../../../constants/entity/permissions';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const DashboardWidgetUserDealTasks: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'My Tasks';

  const {
    currentUser,
    dealListData,
    widgetSettings,
    onWidgetSettingsChange,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();

  const onRowClick: Function = useCallback((dealId: number): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection) {
      const queryParams: LocationQueryParams = {
        [QUERY_PARAM_TAB]: TAB_NAME_DUE_DILIGENCE,
        [QUERY_PARAM_CONTACT_ID]: currentUser.contactId,
      };

      navigate({
        pathname: `${ROUTE_DEAL}/${dealId}`,
        search: FormatService.formatQueryParams(queryParams),
      });
    }
  }, [currentUser]);

  return (
    <Widget
      isScrollable
      withCustomizeMode
      isEmpty={!dealListData || !dealListData.length}
      widgetName={WIDGET_NAME_USER_DEAL_TASKS}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
      widgetSettings={widgetSettings}
      onWidgetSettingsChange={onWidgetSettingsChange}
    >
      <Table>
        <TableBody isPlain withZebraRows withHeaderColumn>
          {
            dealListData.map((dealItem: Deal): ReactElement => (
              <TableRow
                isButton
                key={dealItem.id}
                onClick={(): void => {
                  onRowClick(dealItem.id);
                }}
              >
                <TableCell width="5%">
                  <Image
                    text={dealItem.name}
                    link={dealItem.mainPhotoThumbnail || dealItem.mainPhoto || AssetDefaultImage}
                  />
                </TableCell>
                <TableCell width="45%">
                  <FilteredLink link={`${ROUTE_DEAL}/${dealItem.id}`} permission={DEAL_VIEW}>
                    {dealItem.name}
                  </FilteredLink>
                </TableCell>
                <TableCell>
                  <FlexContainer isColumn isFlexStart>
                    {
                      !!dealItem.tasksTotal && (
                        <FlexContainer>
                          {`${dealItem.tasksTotal} ${dealItem.tasksTotal > 1 ? 'tasks' : 'task'} assigned`}
                        </FlexContainer>
                      )
                    }
                    {
                      !!dealItem.tasksOverdueTotal && (
                        <FlexContainer>
                          <IconBlockExpired />
                          <TextBold>
                            {`${dealItem.tasksOverdueTotal} `
                              + `${dealItem.tasksOverdueTotal > 1 ? 'tasks' : 'task'} overdue`}
                          </TextBold>
                        </FlexContainer>
                      )
                    }
                  </FlexContainer>
                </TableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    </Widget>
  );
});

export default DashboardWidgetUserDealTasks;
